import DataTable from 'react-data-table-component';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import Button from 'src/ui/wards/common/button/Button';
import Input from 'src/ui/wards/common/input/Input';
import Map, { PopupChangedEvent } from 'src/ui/wards/common/map/Map';
import MapLegend from 'src/ui/wards/common/map/MapLegend';
import { workerColumns, workerCustomStyles } from './WorkerTableColumns';
import P_SearchHeader from '../common/searchHeader/P_SearchHeader';
import { useWaUserStore } from 'src/api/wa/store/WaUserStore';
import { useEffect, useMemo, useState } from 'react';
import useWaWorkplaceService from 'src/api/wa/service/WaWorkplaceService';
import { useWaWorkplaceStore } from 'src/api/wa/store/WaWorkplaceStore';
import { useWaFilterStore } from 'src/api/wa/store/WaFilterStore';
import { useWaDisasterStore } from 'src/api/wa/store/WaRegionAndDisasterStore';
import { FormRow } from 'src/ui/wards/common/form/FormRow';
import { LoadingSpinner } from '../common/loading/LoadingSpinner';
import { useNotify } from 'src/ui/common/alert/useNotify';

export const getDistance = (lat1: number, lon1: number, lat2: number, lon2: number) => {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const P5_1_UnaddressedPropertiesMap = () => {
  const { t } = useTranslation();
  const { workplaces, setWorkplaces } = useWaWorkplaceStore();
  const { user, availableWorkers } = useWaUserStore();
  const { selectedWorkplace, setSelectedWorkplace } = useWaWorkplaceStore();
  const [isSearched, setIsSearched] = useState(false);
  const shouldShowNoSitesMessage =
    isSearched && Array.isArray(workplaces) && workplaces.length === 0;
  const [isTimeout, setIsTimeout] = useState(false);
  const { mapCenter, workplaceFilterParameters } = useWaFilterStore();
  const { createWorkplaceReservation, fetchWorkplaces } = useWaWorkplaceService();
  const { selectedDisaster } = useWaDisasterStore();
  const { areMandatorySearchParametersSet } = useWaFilterStore();
  const [openMapPopups, setOpenMapPopups] = useState<PopupChangedEvent[]>([]);
  const [selectedWorkerId, setSelectedWorkerId] = useState<string | null>(null);
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorWorkplace, setErrorWorkplace] = useState<string | null>(null);
  const [propertySubmitAttempted, setPropertySubmitAttempted] = useState(false);
  const [workerSubmitAttempted, setWorkerSubmitAttempted] = useState(false);
  const [isTrafficVisible, setIsTrafficVisible] = useState(false);
  const [recenterTrigger, setRecenterTrigger] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const notify = useNotify();

  useEffect(() => {
    if (selectedDisaster && areMandatorySearchParametersSet() && !isSearched) {
      if (workplaceFilterParameters.branchCode) {
        handleSearch();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDisaster]);

  const handleSearch = async () => {
    setShowErrorMessage(false);
    setErrorWorkplace(null);
    setIsTimeout(false);
    setIsLoading(true);
    setWorkplaces(null);
    try {
      await fetchWorkplaces();
      setIsSearched(true);
      setRecenterTrigger((prev) => prev + 1);
    } catch (error) {
      if (axios.isAxiosError(error) && error.code === 'ECONNABORTED') {
        setIsTimeout(true);
      }
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleProxyReservation = async () => {
    if (isSubmitting) return;

    setShowErrorMessage(false);
    setPropertySubmitAttempted(false);
    setWorkerSubmitAttempted(false);

    if (!selectedWorkplace) {
      setPropertySubmitAttempted(true);
      return;
    }

    if (!selectedWorkerId) {
      setWorkerSubmitAttempted(true);
      return;
    }

    if ((selectedWorkplace.details?.reservations?.length ?? 0) === 0) {
      const reservationParams = [
        {
          engineerUserId: selectedWorkerId,
          branchCode: selectedWorkplace.branchCode,
          customerId: selectedWorkplace.customerId,
        },
      ];

      try {
        setIsSubmitting(true);
        const res = await createWorkplaceReservation(reservationParams);
        if (res) {
          notify('wards.sites.reservationSuccess');
        } else {
          setErrorWorkplace(selectedWorkplace.customerId);
          setShowErrorMessage(true);
        }
      } catch (error) {
        console.log('error ', error);
        setErrorWorkplace(selectedWorkplace.customerId);
        setShowErrorMessage(true);
      } finally {
        setIsSubmitting(false);
      }
    } else {
      notify('wards.sites.allAlreadyReserved');
    }

    setOpenMapPopups([]);
    await fetchWorkplaces();
    setSelectedWorkerId(null);
    setSelectedWorkplace(null);
  };

  const handleWorkerSelected = (workerId: string | null) => {
    setSelectedWorkerId(workerId);
  };

  const sortedWorkers = useMemo(() => {
    const workers = [...availableWorkers];

    if (mapCenter && mapCenter.latitude) {
      workers.sort((a, b) => {
        const hasA = a.location?.latitude != null && a.location?.longitude != null;
        const hasB = b.location?.latitude != null && b.location?.longitude != null;

        if (!hasA && !hasB) return 0;
        if (!hasA) return 1;
        if (!hasB) return -1;

        const distA = getDistance(
          mapCenter.latitude,
          mapCenter.longitude,
          a.location.latitude,
          a.location.longitude
        );
        const distB = getDistance(
          mapCenter.latitude,
          mapCenter.longitude,
          b.location.latitude,
          b.location.longitude
        );
        return distA - distB;
      });
    }

    return workers.map((worker, index) => ({
      ...worker,
      displayIndex: index + 1,
    }));
  }, [availableWorkers, mapCenter]);

  const popupOpened = (event: PopupChangedEvent) => {
    let _openMapPopups = openMapPopups;
    if (event.userId !== undefined) {
      _openMapPopups = openMapPopups.filter((popup) => popup.userId === undefined);
    }
    setOpenMapPopups([..._openMapPopups, event]);
  };

  const popupClosed = (event: PopupChangedEvent) => {
    setOpenMapPopups(openMapPopups.filter((popup) => popup.customerId !== event.customerId));
  };

  useEffect(() => {
    // Reset Workplace and Worker selection for proxy reservation when search conditions change
    setSelectedWorkplace(null);
    setSelectedWorkerId(null);
  }, [workplaceFilterParameters, setSelectedWorkplace]);

  return (
    <div className="h-full space-y-2">
      {selectedDisaster === undefined ? (
        // ローディング中 は、広域災害の情報を取得中であることを示すメッセージを表示する
        <div className="flex h-full items-center justify-center">
          {t('wards.desktop.menu.loadingWideAreaDisaster')}
        </div>
      ) : selectedDisaster ? (
        <>
          <div className="space-y-1 text-2xl font-bold">
            {t('wards.desktop.unaddressedPropertiesMap.title')}
          </div>
          <P_SearchHeader showListButton={true} showMapButton={false} onSearch={handleSearch} />
          {isLoading ? (
            <div className="flex h-96 items-center justify-center">
              <LoadingSpinner />
            </div>
          ) : (
            <>
              {shouldShowNoSitesMessage && (
                <h3 className="mx-2 mb-8 text-center text-xl  text-red-500">
                  {t('wards.common.noResults')}
                </h3>
              )}
              {isTimeout && (
                <h3 className="mx-2 mb-8 text-center text-xl  text-red-500">
                  {t('wards.common.searchTimeout')}
                </h3>
              )}
              {showErrorMessage && (
                <h3 className="mx-2 mb-8 text-center text-xl  text-red-500">
                  {t('wards.sites.reservationFailed', {
                    workplaces: errorWorkplace ? errorWorkplace : '',
                  })}
                </h3>
              )}
              <div className="flex h-full flex-row space-x-2 rounded-md border-t-2 border-sky-600 bg-gray-100 px-4 py-2">
                <div className="flex h-full flex-1 flex-col">
                  <div className="relative flex-1">
                    <Map
                      className="size-full"
                      user={user}
                      workers={sortedWorkers}
                      workplaces={workplaces ?? []}
                      popupClosed={popupClosed}
                      popupOpened={popupOpened}
                      selectedWorkerId={selectedWorkerId}
                      onWorkerSelected={handleWorkerSelected}
                      center={mapCenter}
                      showTraffic={isTrafficVisible}
                      recenterTrigger={recenterTrigger}
                    />
                  </div>

                  <MapLegend className="mt-2 rounded-md border border-gray-200 bg-white p-3 shadow-sm" />
                </div>
                <div className="flex w-96 flex-col space-y-4">
                  <Button
                    title={
                      isTrafficVisible
                        ? t('wards.desktop.unaddressedPropertiesMap.hideTrafficJamInfo')
                        : t('wards.desktop.unaddressedPropertiesMap.trafficJamInfo')
                    }
                    onClick={() => setIsTrafficVisible(!isTrafficVisible)}
                  />
                  <div className="flex flex-col space-y-2 rounded border border-gray-300 p-2">
                    <h3 className="font-semibold">
                      {t('wards.desktop.unaddressedPropertiesMap.proxyReservation')}
                    </h3>
                    <FormRow
                      title={t('wards.desktop.unaddressedPropertiesMap.buildingSite')}
                      layout="vertical"
                    >
                      <Input disabled value={selectedWorkplace?.customerId || ''} />
                    </FormRow>
                    <FormRow
                      title={t('wards.desktop.unaddressedPropertiesMap.worker')}
                      layout="vertical"
                    >
                      <Input
                        disabled
                        value={selectedWorkerId != null ? String(selectedWorkerId) : ''}
                      />
                    </FormRow>
                    <div className="flex justify-end pt-1">
                      <Button
                        title={t('wards.desktop.unaddressedPropertiesMap.proxyReservation')}
                        onClick={handleProxyReservation}
                        disabled={isSubmitting}
                      />
                    </div>

                    {propertySubmitAttempted && !selectedWorkplace && (
                      <p className="text-sm font-medium text-red-600">
                        {t('wards.desktop.unaddressedPropertiesMap.noPropertySelected')}
                      </p>
                    )}
                    {workerSubmitAttempted && !selectedWorkerId && (
                      <p className="text-sm font-medium text-red-600">
                        {t('wards.desktop.unaddressedPropertiesMap.noWorkerSelected')}
                      </p>
                    )}
                  </div>
                  <div>
                    <div className="font-semibold">
                      {t('wards.desktop.unaddressedPropertiesMap.showFe')}
                    </div>
                    <DataTable
                      noDataComponent={t('wards.common.noResults')}
                      noTableHead={true}
                      columns={workerColumns(mapCenter)}
                      data={sortedWorkers.slice(0, 20)}
                      customStyles={workerCustomStyles}
                    />
                  </div>
                </div>
              </div>
            </>
          )}
        </>
      ) : (
        <div className="flex h-full items-center justify-center">
          <h2 className="text-3xl">{t('wards.desktop.menu.noWideAreaDisaster')}</h2>
        </div>
      )}
    </div>
  );
};

export default P5_1_UnaddressedPropertiesMap;
